import { GDPRCookie } from '@wkda/gdprlib';
import { CID_COOKIE_KEY, isProduction, PHP_SESSION_ID_KEY } from 'config';
import { isGdprCookiePermited } from 'helpers/gdprPreferences';
import { LOCALE } from 'helpers/i18n';
import { parseSearch } from 'helpers/location';
import {
  flow,
  includes,
  isEmpty,
  omit,
  pickBy,
  toUpper,
  values,
  pick,
} from 'lodash/fp';
import api from 'store/actions/api';
import Cookies from 'universal-cookie';
import { gdprCookieBannerDuck } from '../../../components/page/Layout/duck';
import { getPhpSessionId } from './selectors';
import {
  getGdprBannerContent,
  isGdprBannerLoaded,
} from '../../../components/page/Layout/selectors';
import { get as getCookie, set as setCookie } from '../cookies';
import {
  appendToQueue,
  initQueue,
  injectExpId,
  PARAMETER_ALIASES,
  stringifyQueue,
} from './cidQueue';
import { isCidValid } from './cidValidator';
import { LlmCidResolver } from './LlmCidResolver';
import * as parameterTypes from './parameterTypes';

const MID_KEY = 'MID';
const COOKIE_EXPIRY_IN_DAYS = 730; // 2 Years
const VALID_QUERY_PARAMS = { ...parameterTypes, [MID_KEY]: MID_KEY };
const VALID_LLM_PARAMS = ['utm_source'];

/**
 *@param {string} query
 * */
function parseLLMQuery(query) {
  return pick(VALID_LLM_PARAMS, parseSearch(query));
}

const CID_LOCALE_MAPPING = {
  'FR-BE': 'B2',
  'NL-BE': 'B1',
  'DE-AT': 'AT',
  'DA': 'DK',
  'SV-SE': 'SE',
};

function getCIDPrefixFromLocale(locale, mapping = CID_LOCALE_MAPPING) {
  locale = toUpper(locale);

  return mapping[locale] || locale;
}

export const DEFAULT_CID = `${getCIDPrefixFromLocale(LOCALE)}_DTI_0_0_0_0_0_0`;

const setSEOCookie = ({
  currentSEOCookie,
  referer,
  cid,
  mid,
  llmCid,
  ...rest
}) => {
  const queue = initQueue(currentSEOCookie);

  let newQueue;

  if (!isEmpty(queue) && referer.isCurrentHost()) {
    return false;
  }

  if (llmCid) {
    newQueue = appendToQueue({
      queue,
      value: llmCid,
      parameters: rest,
      aliases: PARAMETER_ALIASES,
    });
  } else if (mid && isCidValid(mid)) {
    newQueue = appendToQueue({
      queue,
      value: mid,
      parameters: rest,
      aliases: PARAMETER_ALIASES,
    });
  } else if (cid) {
    newQueue = appendToQueue({
      queue,
      value: cid,
      parameters: rest,
      aliases: PARAMETER_ALIASES,
    });
  } else {
    // Add default value
    newQueue = appendToQueue({ queue, value: DEFAULT_CID, parameters: rest });
  }

  return stringifyQueue(newQueue);
};

const extractCookieQueryParams = flow(
  parseSearch,
  pickBy((val, key) => includes(key, values(VALID_QUERY_PARAMS))),
);

/**
 * @param {import('../../../../common/helpers/cookies').CookieService} cookies
 * @returns {boolean}
 */
const isAnalyticalCookiesAllowed = (cookies, state) =>
  function* () {
    try {
      const cookieService = new Cookies();
      const gdprService = yield GDPRCookie.init(
        cookies.getAll(),
        cookieService,
        isProduction,
      );

      if (!isGdprBannerLoaded(state)) {
        yield gdprCookieBannerDuck.tryLoad();
      }

      const gdprBannerContent = getGdprBannerContent(state);

      // Check if analytical cookie is selected or banner partial does not exists.
      return (
        !gdprBannerContent ||
        (gdprBannerContent &&
          gdprService.getUserCategories().includes('analytical'))
      );
    } catch (error) {
      return true;
    }
  };

export const trackCid = ({ cid, expid } = {}) =>
  function* ({ getCurrentLocation, referer, userAgent, getState, cookies }) {
    const { search } = getCurrentLocation();
    const params = extractCookieQueryParams(search);
    const { [MID_KEY]: mid, ...trackingParams } = params;
    const phpSessionIdFromCookies = cookies.get(PHP_SESSION_ID_KEY);
    const phpSessionIdFromResponse = getPhpSessionId(getState());
    const phpSessionId = phpSessionIdFromCookies || phpSessionIdFromResponse;

    const currentSEOCookie = yield getCookie(CID_COOKIE_KEY);

    // LLM detection: fetch config and resolve CID if platform detected
    const llmResolver = LlmCidResolver.create(LOCALE);

    const llmParams = parseLLMQuery(search);

    yield llmResolver.loadConfig();
    const llmCid = llmResolver.resolve(
      {
        utmSource: llmParams.utm_source || null,
        referer: referer.get() || null,
        userAgent: userAgent.get() || null,
      },
      cid,
    );

    const _isAnalyticalCookiesAllowed = yield isAnalyticalCookiesAllowed(
      cookies,
      getState(),
    );

    let cidToSet = setSEOCookie({
      ...trackingParams,
      ...(_isAnalyticalCookiesAllowed ? { expid } : {}),
      currentSEOCookie,
      cid,
      mid,
      llmCid,
      referer,
    });

    if (!currentSEOCookie && phpSessionId) {
      try {
        const { result } = yield api.getCIDSessionData(phpSessionId);

        cidToSet = setSEOCookie({
          ...trackingParams,
          ...(_isAnalyticalCookiesAllowed ? { expid } : {}),
          currentSEOCookie: result.cid,
          cid,
          mid,
          llmCid,
          referer,
        });
      } catch (err) {
        yield api.setCIDSessionData(phpSessionId, cidToSet);
      }
    }

    const isCookiePermitted = yield isGdprCookiePermited(
      cookies.getAll(),
      CID_COOKIE_KEY,
    );

    if (isCookiePermitted) {
      if (cidToSet && cidToSet !== currentSEOCookie) {
        yield setCookie(CID_COOKIE_KEY, cidToSet, COOKIE_EXPIRY_IN_DAYS, {
          crossDomain: true,
          httpOnly: false,
        });
      }
    }
  };

function setInspectionSEOCookie({
  currentSEOCookie,
  referer,
  cid,
  mid,
  ...rest
}) {
  const queue = initQueue(currentSEOCookie);

  let newQueue;

  if (isEmpty(queue)) {
    if (mid && isCidValid(mid)) {
      newQueue = appendToQueue({
        queue,
        value: mid,
        parameters: rest,
        aliases: PARAMETER_ALIASES,
      });
    } else if (cid) {
      newQueue = appendToQueue({
        queue,
        value: cid,
        parameters: rest,
        aliases: PARAMETER_ALIASES,
      });
    } else {
      // Add default value
      newQueue = appendToQueue({ queue, value: DEFAULT_CID, parameters: rest });
    }
  } else {
    newQueue = injectExpId(queue, rest.expid);
  }

  return stringifyQueue(newQueue);
}

export const trackInspectionCID = ({ cid, expid } = {}) => {
  return function* ({ getCurrentLocation, referer, getState, cookies }) {
    const { search } = getCurrentLocation();
    const params = extractCookieQueryParams(search);
    const { [MID_KEY]: mid, ...trackingParams } = params;
    const allParams = omit(['utm_expid'], trackingParams || {});
    const phpSessionIdFromCookies = cookies.get(PHP_SESSION_ID_KEY);
    const phpSessionIdFromResponse = getPhpSessionId(getState());
    const phpSessionId = phpSessionIdFromCookies || phpSessionIdFromResponse;

    const _isAnalyticalCookiesAllowed = yield isAnalyticalCookiesAllowed(
      cookies,
      getState(),
    );

    const currentSEOCookie = cookies.get(CID_COOKIE_KEY);

    let cidToSet = setInspectionSEOCookie({
      ...allParams,
      ...(_isAnalyticalCookiesAllowed ? { expid } : {}),
      currentSEOCookie,
      cid,
      mid,
      referer,
    });

    if (!currentSEOCookie && phpSessionId) {
      try {
        const { result } = yield api.getCIDSessionData(phpSessionId);

        cidToSet = setInspectionSEOCookie({
          ...allParams,
          ...(_isAnalyticalCookiesAllowed ? { expid } : {}),
          currentSEOCookie: result.cid,
          cid,
          mid,
          referer,
        });
      } catch (err) {
        yield api.setCIDSessionData(phpSessionId, cidToSet);
      }
    }

    if (cidToSet && cidToSet !== currentSEOCookie) {
      yield setCookie(CID_COOKIE_KEY, cidToSet, COOKIE_EXPIRY_IN_DAYS, {
        crossDomain: true,
        httpOnly: false,
      });
    }
  };
};
